/**
 * Call-link primitives — Signal's "join via link" model, web port.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/CallLink.swift`.
 *
 * The link carries a 32-byte secret out-of-band. The room id the server
 * indexes by is HKDF-SHA256(secret, salt=zeros32, info="kaether:call:roomid:v1")
 * truncated to 16 bytes, so a server log leak exposes only the room id, never
 * the secret. The link also carries the owner's Ed25519 public key plus a 64-byte
 * Ed25519 signature over the canonical bytes of the link payload; receivers refuse
 * links whose signature does not verify, so the server cannot synthesise a link
 * or rewrite a field of it.
 *
 * Wire format:
 *   kaether:call:link:v1:<base64url(payload)>
 *
 * Payload byte layout (all multi-byte integers little-endian):
 *   offset  0 — 1 byte   schema (0x01 for v1)
 *   offset  1 — 8 bytes  createdAt (Int64 LE)
 *   offset  9 — 1 byte   flags (bit 0 = approvalRequired, bit 1 = videoDefault)
 *   offset 10 — 2 bytes  ownerUserIdLen (UInt16 LE, byte count of UTF-8)
 *   offset 12 — N bytes  ownerUserId (UTF-8)
 *   offset 12+N — 32 bytes secret
 *   offset 44+N — 32 bytes ownerPublicKey (Ed25519 raw)
 *   offset 76+N — 64 bytes signature (Ed25519 over bytes[0..76+N))
 *
 * Pure logic / SubtleCrypto only. No SolidJS imports.
 * Mirrors iOS byte-for-byte; HKDF info strings and binary layout are identical.
 */

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

export type CallLinkErrorKind =
  | { tag: "invalidSecretLength"; got: number }
  | { tag: "invalidRoomIdLength"; got: number }
  | { tag: "invalidOwnerPublicKeyLength"; got: number }
  | { tag: "invalidSignatureLength"; got: number }
  | { tag: "malformedLink"; reason: string }
  | { tag: "unsupportedSchema"; schema: number }
  | { tag: "invalidSignature" };

export class CallLinkError extends Error {
  readonly kind: CallLinkErrorKind;

  constructor(kind: CallLinkErrorKind) {
    super(CallLinkError.message(kind));
    this.name = "CallLinkError";
    this.kind = kind;
  }

  private static message(k: CallLinkErrorKind): string {
    switch (k.tag) {
      case "invalidSecretLength":
        return `invalid secret length: expected 32, got ${k.got}`;
      case "invalidRoomIdLength":
        return `invalid room id length: expected 16, got ${k.got}`;
      case "invalidOwnerPublicKeyLength":
        return `invalid owner public key length: expected 32, got ${k.got}`;
      case "invalidSignatureLength":
        return `invalid signature length: expected 64, got ${k.got}`;
      case "malformedLink":
        return `malformed call link: ${k.reason}`;
      case "unsupportedSchema":
        return `unsupported call link schema: ${k.schema}`;
      case "invalidSignature":
        return "invalid owner signature";
    }
  }
}

// ---------------------------------------------------------------------------
// Value objects
// ---------------------------------------------------------------------------

/**
 * 32-byte secret embedded in a call link. Whoever holds it can derive the
 * room id and (with the rest of the link) join the call. The server never
 * sees this value.
 */
export class CallLinkSecret {
  readonly raw: Uint8Array;

  constructor(raw: Uint8Array) {
    if (raw.length !== 32) {
      throw new CallLinkError({ tag: "invalidSecretLength", got: raw.length });
    }
    this.raw = raw.slice();
  }

  /** Cryptographically-secure 32-byte random secret. */
  static generate(): CallLinkSecret {
    const raw = new Uint8Array(32);
    crypto.getRandomValues(raw);
    return new CallLinkSecret(raw);
  }

  equals(other: CallLinkSecret): boolean {
    if (this.raw.length !== other.raw.length) return false;
    let diff = 0;
    for (let i = 0; i < this.raw.length; i++) {
      diff |= this.raw[i] ^ other.raw[i];
    }
    return diff === 0;
  }
}

/**
 * 16-byte deterministic identifier derivable from the secret. Shared with
 * the server as an opaque handle for the call room.
 */
export class CallLinkRoomId {
  readonly raw: Uint8Array;

  constructor(raw: Uint8Array) {
    if (raw.length !== 16) {
      throw new CallLinkError({ tag: "invalidRoomIdLength", got: raw.length });
    }
    this.raw = raw.slice();
  }

  /** base64url with no padding — safe for URLs, QR codes, and log keys. */
  asString(): string {
    return base64UrlEncode(this.raw);
  }

  equals(other: CallLinkRoomId): boolean {
    if (this.raw.length !== other.raw.length) return false;
    let diff = 0;
    for (let i = 0; i < this.raw.length; i++) {
      diff |= this.raw[i] ^ other.raw[i];
    }
    return diff === 0;
  }
}

/**
 * Flags occupying one wire byte. Bit 0 = approvalRequired, bit 1 = videoDefault.
 * The server cannot rewrite flags without breaking the owner signature.
 */
export const CallLinkFlags = {
  none: 0 as const,
  approvalRequired: 1 << 0,
  videoDefault: 1 << 1,
} as const;

export type CallLinkFlagsValue = number;

/**
 * Decoded call link: secret + owner public key + owner userId + flags +
 * createdAt + a 64-byte Ed25519 signature binding all of them.
 */
export interface CallLink {
  readonly secret: CallLinkSecret;
  readonly ownerPublicKey: Uint8Array; // 32-byte Ed25519 raw
  readonly ownerUserId: string;
  readonly flags: CallLinkFlagsValue;
  readonly createdAt: bigint; // Int64 as BigInt for exact LE encoding
  readonly signature: Uint8Array; // 64-byte Ed25519 sig over canonical bytes
}

// ---------------------------------------------------------------------------
// HKDF info string (CRITICAL — must match iOS byte-for-byte)
// ---------------------------------------------------------------------------

const HKDF_INFO_ROOM_ID = "kaether:call:roomid:v1";
const HKDF_SALT = new Uint8Array(32); // 32 zero bytes

// ---------------------------------------------------------------------------
// Crypto helpers
// ---------------------------------------------------------------------------

const ALG_ED25519 = { name: "Ed25519" } as const;

/**
 * HKDF-SHA256(secret, salt=zeros32, info="kaether:call:roomid:v1")
 * truncated to 16 bytes. Deterministic — every holder of the secret derives
 * the same room id. Mirrors iOS `CallLinkCrypto.deriveRoomId`.
 */
export async function deriveRoomId(
  secret: CallLinkSecret,
): Promise<CallLinkRoomId> {
  const ikm = await crypto.subtle.importKey(
    "raw",
    secret.raw,
    { name: "HKDF" },
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: HKDF_SALT,
      info: new TextEncoder().encode(HKDF_INFO_ROOM_ID),
    },
    ikm,
    128, // 16 bytes * 8 bits
  );
  return new CallLinkRoomId(new Uint8Array(bits));
}

/**
 * Canonical byte layout signed by the owner. Mirrors iOS
 * `CallLinkCrypto.canonicalBytesForSignature` exactly:
 *
 *   schema(1) ‖ createdAt(LE8) ‖ flags(1) ‖ ownerUserIdLen(LE2) ‖
 *   ownerUserId(UTF-8) ‖ secret(32) ‖ ownerPublicKey(32)
 *
 * The signature itself is NOT included (as it covers these bytes).
 */
export function canonicalBytesForSignature(
  schema: number,
  createdAt: bigint,
  flags: CallLinkFlagsValue,
  ownerUserId: string,
  secret: CallLinkSecret,
  ownerPublicKey: Uint8Array,
): Uint8Array {
  const enc = new TextEncoder();
  const userIdBytes = enc.encode(ownerUserId);
  // schema(1) + createdAt(8) + flags(1) + userIdLen(2) + userId(N) + secret(32) + pub(32)
  const totalLen = 1 + 8 + 1 + 2 + userIdBytes.length + 32 + 32;
  const buf = new Uint8Array(totalLen);
  let pos = 0;

  buf[pos++] = schema & 0xff;

  // createdAt as 8-byte LE (BigInt)
  writeInt64LE(buf, pos, createdAt);
  pos += 8;

  buf[pos++] = flags & 0xff;

  // ownerUserIdLen as 2-byte LE
  const idLen = userIdBytes.length;
  buf[pos++] = idLen & 0xff;
  buf[pos++] = (idLen >> 8) & 0xff;

  buf.set(userIdBytes, pos);
  pos += userIdBytes.length;

  buf.set(secret.raw, pos);
  pos += 32;

  buf.set(ownerPublicKey, pos);

  return buf;
}

/**
 * Sign the canonical bytes with `privateKey` (raw 32-byte Ed25519 seed).
 * Returns a 64-byte Ed25519 signature.
 */
export async function signCallLink(
  schema: number,
  createdAt: bigint,
  flags: CallLinkFlagsValue,
  ownerUserId: string,
  secret: CallLinkSecret,
  ownerPublicKey: Uint8Array,
  privateKey: CryptoKey,
): Promise<Uint8Array> {
  const bytes = canonicalBytesForSignature(
    schema,
    createdAt,
    flags,
    ownerUserId,
    secret,
    ownerPublicKey,
  );
  const sigBuf = await crypto.subtle.sign(ALG_ED25519, privateKey, bytes);
  return new Uint8Array(sigBuf);
}

/**
 * Verify that `link.signature` was produced by the private key matching
 * `link.ownerPublicKey` over the canonical bytes of `link`. Returns false on
 * any structural problem so callers can fail-closed. Mirrors iOS
 * `CallLinkCrypto.verify`.
 */
export async function verifyCallLink(link: CallLink): Promise<boolean> {
  if (link.signature.length !== 64) return false;
  let pub: CryptoKey;
  try {
    pub = await crypto.subtle.importKey(
      "raw",
      link.ownerPublicKey,
      ALG_ED25519,
      false,
      ["verify"],
    );
  } catch {
    return false;
  }
  const bytes = canonicalBytesForSignature(
    CALL_LINK_CURRENT_SCHEMA,
    link.createdAt,
    link.flags,
    link.ownerUserId,
    link.secret,
    link.ownerPublicKey,
  );
  try {
    return await crypto.subtle.verify(ALG_ED25519, pub, link.signature, bytes);
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

export const CALL_LINK_PREFIX = "kaether:call:link:v1:" as const;
export const CALL_LINK_CURRENT_SCHEMA = 1;

/**
 * Cheap prefix check — does not validate the payload. Mirrors iOS
 * `CallLinkCodec.isCallLink`.
 */
export function isCallLink(s: string): boolean {
  return s.startsWith(CALL_LINK_PREFIX);
}

/**
 * Encode a CallLink to the canonical wire string. Mirrors iOS
 * `CallLinkCodec.encode` byte-for-byte.
 */
export function encodeCallLink(link: CallLink): string {
  const enc = new TextEncoder();
  const userIdBytes = enc.encode(link.ownerUserId);
  // schema(1) + createdAt(8) + flags(1) + userIdLen(2) + userId(N) + secret(32) + pub(32) + sig(64)
  const totalLen = 1 + 8 + 1 + 2 + userIdBytes.length + 32 + 32 + 64;
  const buf = new Uint8Array(totalLen);
  let pos = 0;

  buf[pos++] = CALL_LINK_CURRENT_SCHEMA & 0xff;

  writeInt64LE(buf, pos, link.createdAt);
  pos += 8;

  buf[pos++] = link.flags & 0xff;

  const idLen = userIdBytes.length;
  buf[pos++] = idLen & 0xff;
  buf[pos++] = (idLen >> 8) & 0xff;

  buf.set(userIdBytes, pos);
  pos += userIdBytes.length;

  buf.set(link.secret.raw, pos);
  pos += 32;

  buf.set(link.ownerPublicKey, pos);
  pos += 32;

  buf.set(link.signature, pos);

  return CALL_LINK_PREFIX + base64UrlEncode(buf);
}

/**
 * Parse a call link string, verify the owner signature, and return the
 * decoded `CallLink`. Mirrors iOS `CallLinkCodec.decode`.
 *
 * Throws `CallLinkError` on any validation or signature failure.
 */
export async function decodeCallLink(s: string): Promise<CallLink> {
  if (!s.startsWith(CALL_LINK_PREFIX)) {
    throw new CallLinkError({ tag: "malformedLink", reason: "missing prefix" });
  }
  const b64 = s.slice(CALL_LINK_PREFIX.length);
  const data = base64UrlDecode(b64);
  if (data === null) {
    throw new CallLinkError({
      tag: "malformedLink",
      reason: "invalid base64url",
    });
  }

  // Minimum: schema(1) + createdAt(8) + flags(1) + userIdLen(2) + secret(32) + pub(32) + sig(64)
  const minLen = 1 + 8 + 1 + 2 + 32 + 32 + 64;
  if (data.length < minLen) {
    throw new CallLinkError({
      tag: "malformedLink",
      reason: `payload too short: ${data.length} < ${minLen}`,
    });
  }

  let pos = 0;

  const schema = data[pos++];
  if (schema !== CALL_LINK_CURRENT_SCHEMA) {
    throw new CallLinkError({ tag: "unsupportedSchema", schema });
  }

  const createdAt = readInt64LE(data, pos);
  pos += 8;

  const flags = data[pos++];

  const idLen = readUInt16LE(data, pos);
  pos += 2;

  const expectedLen = minLen + idLen;
  if (data.length !== expectedLen) {
    throw new CallLinkError({
      tag: "malformedLink",
      reason: `payload length ${data.length} !== expected ${expectedLen}`,
    });
  }

  const userIdBytes = data.slice(pos, pos + idLen);
  let ownerUserId: string;
  try {
    ownerUserId = new TextDecoder("utf-8", { fatal: true }).decode(userIdBytes);
  } catch {
    throw new CallLinkError({
      tag: "malformedLink",
      reason: "ownerUserId is not valid UTF-8",
    });
  }
  pos += idLen;

  const secretBytes = data.slice(pos, pos + 32);
  if (secretBytes.length !== 32) {
    throw new CallLinkError({
      tag: "invalidSecretLength",
      got: secretBytes.length,
    });
  }
  pos += 32;

  const pubBytes = data.slice(pos, pos + 32);
  if (pubBytes.length !== 32) {
    throw new CallLinkError({
      tag: "invalidOwnerPublicKeyLength",
      got: pubBytes.length,
    });
  }
  pos += 32;

  const sigBytes = data.slice(pos, pos + 64);
  if (sigBytes.length !== 64) {
    throw new CallLinkError({
      tag: "invalidSignatureLength",
      got: sigBytes.length,
    });
  }

  let secret: CallLinkSecret;
  try {
    secret = new CallLinkSecret(secretBytes);
  } catch {
    throw new CallLinkError({ tag: "invalidSecretLength", got: secretBytes.length });
  }

  const link: CallLink = {
    secret,
    ownerPublicKey: pubBytes,
    ownerUserId,
    flags,
    createdAt,
    signature: sigBytes,
  };

  const valid = await verifyCallLink(link);
  if (!valid) {
    throw new CallLinkError({ tag: "invalidSignature" });
  }

  return link;
}

/**
 * Convenience: returns null for non-call-link strings. Mirrors iOS
 * `CallLinkCodec.tryDecode`.
 */
export async function tryDecodeCallLink(s: string): Promise<CallLink | null> {
  if (!isCallLink(s)) return null;
  try {
    return await decodeCallLink(s);
  } catch {
    return null;
  }
}

/**
 * Generate a call link from an owner Ed25519 keypair.
 *
 * `ownerPrivateKey` must be the raw 32-byte Ed25519 seed as a CryptoKey
 * with `["sign"]` usage, `ownerPublicKeyBytes` the corresponding 32-byte
 * raw public key.
 */
export async function generateCallLink(
  ownerPublicKeyBytes: Uint8Array,
  ownerPrivateKey: CryptoKey,
  ownerUserId: string,
  flags: CallLinkFlagsValue = CallLinkFlags.none,
  createdAt: bigint = BigInt(Math.floor(Date.now() / 1000)),
): Promise<CallLink> {
  const secret = CallLinkSecret.generate();
  const signature = await signCallLink(
    CALL_LINK_CURRENT_SCHEMA,
    createdAt,
    flags,
    ownerUserId,
    secret,
    ownerPublicKeyBytes,
    ownerPrivateKey,
  );
  return {
    secret,
    ownerPublicKey: ownerPublicKeyBytes.slice(),
    ownerUserId,
    flags,
    createdAt,
    signature,
  };
}

/**
 * Generate a fresh Ed25519 key pair suitable for use as a call link owner key.
 * Returns both CryptoKey handles and the raw 32-byte public key bytes.
 */
export async function generateOwnerKeyPair(): Promise<{
  publicKey: CryptoKey;
  privateKey: CryptoKey;
  publicKeyBytes: Uint8Array;
}> {
  const kp = await crypto.subtle.generateKey(ALG_ED25519, true, [
    "sign",
    "verify",
  ]);
  const pubBuf = await crypto.subtle.exportKey("raw", kp.publicKey);
  return {
    publicKey: kp.publicKey,
    privateKey: kp.privateKey,
    publicKeyBytes: new Uint8Array(pubBuf),
  };
}

// ---------------------------------------------------------------------------
// Internal helpers (shared with CallLinkRevocation)
// ---------------------------------------------------------------------------

export function base64UrlEncode(data: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

export function base64UrlDecode(s: string): Uint8Array | null {
  try {
    const padded =
      s.replace(/-/g, "+").replace(/_/g, "/") +
      "====".slice((s.length % 4 === 0 ? 4 : s.length % 4));
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}

/** Write a BigInt as 8-byte little-endian into `buf` at `offset`. */
function writeInt64LE(buf: Uint8Array, offset: number, value: bigint): void {
  let v = value;
  for (let i = 0; i < 8; i++) {
    buf[offset + i] = Number(v & 0xffn);
    v >>= 8n;
  }
}

/** Read 8 bytes little-endian at `offset` as a BigInt (signed). */
function readInt64LE(buf: Uint8Array, offset: number): bigint {
  let v = 0n;
  for (let i = 7; i >= 0; i--) {
    v = (v << 8n) | BigInt(buf[offset + i]);
  }
  // Treat as signed 64-bit: if bit 63 is set, reinterpret.
  if (v >= 0x8000_0000_0000_0000n) {
    v = v - 0x1_0000_0000_0000_0000n;
  }
  return v;
}

/** Read 2 bytes little-endian at `offset` as an unsigned 16-bit integer. */
function readUInt16LE(buf: Uint8Array, offset: number): number {
  return buf[offset] | (buf[offset + 1] << 8);
}
